import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  collection, 
  getDoc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  query, 
  orderBy, 
  limit, 
  startAfter, 
  where,
  runTransaction,
  Timestamp,
  doc as firestoreDoc
} from 'firebase/firestore';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faStar, 
  faStarHalfAlt, 
  faEdit, 
  faTrash, 
  faReply, 
  faSpinner, 
  faCheck, 
  faTimes, 
  faPlus,
  faFilter,
  faChevronDown,
  faChevronUp
} from '@fortawesome/free-solid-svg-icons';
import { faStar as farStar } from '@fortawesome/free-regular-svg-icons';
import UserContext from '../../context/UserContext';
import { getFirestoreInstance } from '../../utils/firebaseHelper';
import styles from './ProfileComponents.module.css';
import defaultUserImage from '../../assets/Images/defaultUserProfilePicture.png';
import verifiedBadge from '../../assets/Images/profileverifiedtick.png';
import { createNotification } from '../../utils/NotificationService';

const RatingsReviewsSection = ({ profileData: initialProfileData, isOwnProfile, showCustomAlert }) => {
  const { userData } = useContext(UserContext);
  const navigate = useNavigate();
  // Use local state for profileData to allow updates without requiring parent refresh
  const [profileData, setProfileData] = useState(initialProfileData);
  
  // State for ratings and reviews
  const [reviews, setReviews] = useState([]);
  const [displayedReviews, setDisplayedReviews] = useState([]);
  const [lastReviewDoc, setLastReviewDoc] = useState(null);
  const [hasMoreReviews, setHasMoreReviews] = useState(true);
  const [loadingReviews, setLoadingReviews] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  // State for tag-based caching
  const [tagFilteredReviews, setTagFilteredReviews] = useState({});
  const [tagLastReviewDocs, setTagLastReviewDocs] = useState({});
  const [tagHasMoreReviews, setTagHasMoreReviews] = useState({});
  
  // Form state
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  
  // Filter state
  const [activeTagFilter, setActiveTagFilter] = useState('all');
  const [availableTags, setAvailableTags] = useState([]);

  const [isSingleItemView, setIsSingleItemView] = useState(false);
const [singleReview, setSingleReview] = useState(null);
const [singleItemId, setSingleItemId] = useState(null);
  
  // Rating stars UI state
  const [userReview, setUserReview] = useState({
    rating: 0,
    comment: '',
    tags: {}
  });
  const [hoverRating, setHoverRating] = useState(0);
  
  // Define tag categories (these should be tailored to Organization/Vendor)
  const tagCategories = {
    experience: {
      title: "Experience",
      tags: [
        "Responsive Communication",
        "Professional",
        "Poor Communication",
        "Reliable",
        "Unreliable",
        "Transparent Pricing",
        "Hidden Fees"
      ]
    },
    quality: {
      title: "Quality",
      tags: [
        "High Quality",
        "Average Quality",
        "Low Quality",
        "Excellent Service",
        "Poor Service",
        "Value for Money",
        "Overpriced"
      ]
    },
    timeline: {
      title: "Timeline",
      tags: [
        "On-Time Delivery",
        "Delayed Delivery",
        "Quick Response",
        "Slow Response",
        "Met Deadlines",
        "Missed Deadlines"
      ]
    },
    support: {
      title: "Support",
      tags: [
        "Great Support",
        "Poor Support",
        "Helpful Staff",
        "Unhelpful Staff",
        "Friendly Team",
        "Unfriendly Team"
      ]
    }
  };
  
  // Update local profileData when props change
  useEffect(() => {
    setProfileData(initialProfileData);
  }, [initialProfileData]);
  
  // Helper function to calculate the average rating
  const calculateAverageRating = (ratings) => {
    if (!ratings || !ratings.totalRatingsCount || ratings.totalRatingsCount === 0) {
      return 0;
    }
    return ratings.totalRatingsScore / ratings.totalRatingsCount;
  };
  
  // Helper function to convert tags object to array
  const convertTagsToArray = (tagsObject) => {
    return Object.entries(tagsObject)
      .filter(([_, selected]) => selected === true)
      .map(([tag, _]) => tag);
  };
  
  // Helper function to convert tags array to object
  const convertTagsToObject = (tagsArray) => {
    if (!tagsArray || !Array.isArray(tagsArray)) return {};
    
    const tagsObject = {};
    tagsArray.forEach(tag => {
      tagsObject[tag] = true;
    });
    return tagsObject;
  };
  
  // Clear cache for all tag filters
  const clearTagCache = () => {
    setTagFilteredReviews({});
    setTagLastReviewDocs({});
    setTagHasMoreReviews({});
  };

  const navigateToUserProfile = (userName) => {
    if (userName) {
      navigate(`/${userName}`);
    }
  };
  
  // Load reviews with caching support
  const loadReviews = useCallback(async (isInitial = false, tagFilter = 'all', forceRefresh = false) => {
    if (!profileData?.userId) return;
    
    // Set loading state
    setLoadingReviews(true);
    
    try {
      // Check if we have cached data for this tag and it's not a forced refresh
      if (!forceRefresh && isInitial && tagFilteredReviews[tagFilter] && tagFilteredReviews[tagFilter].length > 0) {
        // Use cached data
        setDisplayedReviews(tagFilteredReviews[tagFilter]);
        setLastReviewDoc(tagLastReviewDocs[tagFilter]);
        setHasMoreReviews(tagHasMoreReviews[tagFilter] || false);
        
        // Set active tag filter if it changed
        if (tagFilter !== activeTagFilter) {
          setActiveTagFilter(tagFilter);
        }
        
        setLoadingReviews(false);
        return;
      }
      
      const db = getFirestoreInstance();
      const reviewsRef = collection(db, 'users', profileData.userId, 'reviews');
      
      let reviewsQuery;
      
      // Apply tag filter if not 'all'
      if (tagFilter !== 'all') {
        // Use array-contains to filter by tag
        if (isInitial) {
          reviewsQuery = query(
            reviewsRef,
            where("tags", "array-contains", tagFilter),
            orderBy('timestamp', 'desc'),
            limit(5)
          );
        } else if (tagLastReviewDocs[tagFilter]) {
          reviewsQuery = query(
            reviewsRef,
            where("tags", "array-contains", tagFilter),
            orderBy('timestamp', 'desc'),
            startAfter(tagLastReviewDocs[tagFilter]),
            limit(5)
          );
        } else {
          setHasMoreReviews(false);
          setLoadingReviews(false);
          return;
        }
      } else {
        // No tag filter
        if (isInitial) {
          reviewsQuery = query(
            reviewsRef,
            orderBy('timestamp', 'desc'),
            limit(5)
          );
        } else if (tagLastReviewDocs[tagFilter]) {
          reviewsQuery = query(
            reviewsRef,
            orderBy('timestamp', 'desc'),
            startAfter(tagLastReviewDocs[tagFilter]),
            limit(5)
          );
        } else {
          setHasMoreReviews(false);
          setLoadingReviews(false);
          return;
        }
      }
      
      const reviewsSnapshot = await getDocs(reviewsQuery);
      
      // Update hasMoreReviews state
      const hasMore = reviewsSnapshot.docs.length === 5;
      
      // Update tag-specific hasMore state
      setTagHasMoreReviews(prev => ({
        ...prev,
        [tagFilter]: hasMore
      }));
      
      setHasMoreReviews(hasMore);
      
      if (reviewsSnapshot.empty) {
        if (isInitial) {
          setDisplayedReviews([]);
          
          // Update tag-specific cache with empty array
          setTagFilteredReviews(prev => ({
            ...prev,
            [tagFilter]: []
          }));
        }
        
        setHasMoreReviews(false);
        setTagHasMoreReviews(prev => ({
          ...prev,
          [tagFilter]: false
        }));
      } else {
        // Get the last document
        const lastDoc = reviewsSnapshot.docs[reviewsSnapshot.docs.length - 1];
        
        // Update tag-specific lastDoc state
        setTagLastReviewDocs(prev => ({
          ...prev,
          [tagFilter]: lastDoc
        }));
        
        setLastReviewDoc(lastDoc);
        
        // Process reviews and fetch user data
        const reviewsData = await Promise.all(
          reviewsSnapshot.docs.map(async (reviewDoc) => {
            const data = reviewDoc.data();
            
            // Convert tags array to object for consistent UI handling
            const reviewTags = convertTagsToObject(data.tags);
            
            // Try to get user data
            let userDataObj = null;
            
            try {
              // Fetch user data
              const userDocRef = firestoreDoc(db, 'users', data.userId);
              const userDoc = await getDoc(userDocRef);
              
              if (userDoc.exists()) {
                const userData = userDoc.data();
                userDataObj = {
                  fullName: userData.fullName || '',
                  userName: userData.userName || '',
                  profilePictureUrl: userData.profilePictureUrl || '',
                  userSubType: userData.userSubType || '',
                  idVerificationStatus: userData.idVerificationStatus || ''
                };
              }
              
              // Fetch replies if any
              const repliesRef = collection(db, 'users', profileData.userId, 'reviews', reviewDoc.id, 'replies');
              const repliesQuery = query(repliesRef, orderBy('timestamp', 'asc'), limit(2));
              const repliesSnapshot = await getDocs(repliesQuery);
              
              const replies = await Promise.all(
                repliesSnapshot.docs.map(async (replyDoc) => {
                  const replyData = replyDoc.data();
                  let replyUserData = null;
                  
                  // Fetch reply user data
                  try {
                    const replyUserDocRef = firestoreDoc(db, 'users', replyData.userId);
                    const replyUserDoc = await getDoc(replyUserDocRef);
                    
                    if (replyUserDoc.exists()) {
                      const userData = replyUserDoc.data();
                      replyUserData = {
                        fullName: userData.fullName || '',
                        userName: userData.userName || '',
                        profilePictureUrl: userData.profilePictureUrl || '',
                        userSubType: userData.userSubType || '',
                        idVerificationStatus: userData.idVerificationStatus || ''
                      };
                    }
                  } catch (err) {
                    console.error('Error fetching reply user data:', err);
                  }
                  
                  return {
                    id: replyDoc.id,
                    ...replyData,
                    user: replyUserData || {
                      fullName: 'Unknown User',
                      userName: '',
                      profilePictureUrl: '',
                      userSubType: '',
                      idVerificationStatus: ''
                    }
                  };
                })
              );
              
              // Check if there are more replies
              const hasMoreReplies = repliesSnapshot.docs.length === 2;
              const repliesCount = data.repliesCount || 0;
              
              return {
                id: reviewDoc.id,
                ...data,
                tags: reviewTags, // Store tags as object for UI
                tagsArray: data.tags || [], // Keep the original array
                user: userDataObj || {
                  fullName: 'Unknown User',
                  userName: '',
                  profilePictureUrl: '',
                  userSubType: '',
                  idVerificationStatus: ''
                },
                replies: replies,
                hasMoreReplies: hasMoreReplies,
                showReplies: replies.length > 0,
                loadingMoreReplies: false,
                repliesCount: repliesCount,
                lastReplyDoc: repliesSnapshot.docs.length > 0 ? 
                  repliesSnapshot.docs[repliesSnapshot.docs.length - 1] : null
              };
            } catch (err) {
              console.error('Error fetching user data:', err);
              
              return {
                id: reviewDoc.id,
                ...data,
                tags: reviewTags,
                tagsArray: data.tags || [],
                user: {
                  fullName: 'Unknown User',
                  userName: '',
                  profilePictureUrl: '',
                  userSubType: '',
                  idVerificationStatus: ''
                },
                replies: [],
                hasMoreReplies: false,
                showReplies: false,
                loadingMoreReplies: false,
                repliesCount: 0,
                lastReplyDoc: null
              };
            }
          })
        );
        
        if (isInitial) {
          setDisplayedReviews(reviewsData);
          
          // Update tag-specific cache
          setTagFilteredReviews(prev => ({
            ...prev,
            [tagFilter]: reviewsData
          }));
          
          // If this is the initial load of all reviews, update the full reviews list too
          if (tagFilter === 'all') {
            setReviews(reviewsData);
          }
        } else {
          const newDisplayedReviews = [...displayedReviews, ...reviewsData];
          setDisplayedReviews(newDisplayedReviews);
          
          // Update tag-specific cache for "load more"
          setTagFilteredReviews(prev => ({
            ...prev,
            [tagFilter]: newDisplayedReviews
          }));
          
          // If loading more for 'all' tag, update the full reviews list too
          if (tagFilter === 'all') {
            setReviews(prev => [...prev, ...reviewsData]);
          }
        }
      }
      
      // Set active tag filter if it changed
      if (tagFilter !== activeTagFilter) {
        setActiveTagFilter(tagFilter);
      }
      
      // Extract available tags if initial load
      if (isInitial && (tagFilter === 'all' || !availableTags.length)) {
        fetchAvailableTags();
      }
      
    } catch (err) {
      console.error('Error loading reviews:', err);
      showCustomAlert({
        title: 'Error',
        message: 'Failed to load reviews. Please try again.',
        type: 'error'
      });
    } finally {
      setLoadingReviews(false);
    }
  }, [profileData?.userId, tagLastReviewDocs, tagFilteredReviews, activeTagFilter, availableTags.length, displayedReviews, showCustomAlert]);
  
  // Extract available tags from all reviews
  const fetchAvailableTags = useCallback(async () => {
    if (!profileData?.userId) return;
    
    try {
      const db = getFirestoreInstance();
      const reviewsRef = collection(db, 'users', profileData.userId, 'reviews');
      const reviewsSnapshot = await getDocs(reviewsRef);
      
      // Collect all tags used in reviews
      const tagsUsage = {};
      
      reviewsSnapshot.docs.forEach(doc => {
        const data = doc.data();
        if (data.tags && Array.isArray(data.tags)) {
          data.tags.forEach(tag => {
            tagsUsage[tag] = (tagsUsage[tag] || 0) + 1;
          });
        }
      });
      
      // Sort tags by usage count (descending)
      const sortedTags = Object.entries(tagsUsage)
        .sort((a, b) => b[1] - a[1])
        .map(([tag]) => tag);
      
      setAvailableTags(sortedTags);
    } catch (err) {
      console.error('Error fetching tags:', err);
    }
  }, [profileData?.userId]);
  
 // Add this new function to fetch a single review item by ID
const fetchSingleReviewById = useCallback(async (reviewId) => {
  if (!profileData?.userId || !reviewId) return;
  
  try {
    setIsLoading(true);
    
    const db = getFirestoreInstance();
    const reviewRef = firestoreDoc(db, 'users', profileData.userId, 'reviews', reviewId);
    const reviewDoc = await getDoc(reviewRef);
    
    if (!reviewDoc.exists()) {
      showCustomAlert({
        title: 'Error',
        message: 'The review you\'re looking for could not be found.',
        type: 'error'
      });
      setIsLoading(false);
      return;
    }
    
    const data = reviewDoc.data();
    
    // Convert tags array to object for consistent UI handling
    const reviewTags = convertTagsToObject(data.tags);
    
    // Try to get user data
    let userDataObj = null;
    
    try {
      // Fetch user data
      const userDocRef = firestoreDoc(db, 'users', data.userId);
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        userDataObj = {
          fullName: userData.fullName || '',
          userName: userData.userName || '',
          profilePictureUrl: userData.profilePictureUrl || '',
          userSubType: userData.userSubType || '',
          idVerificationStatus: userData.idVerificationStatus || ''
        };
      }
      
      // Fetch replies if any
      const repliesRef = collection(db, 'users', profileData.userId, 'reviews', reviewId, 'replies');
      const repliesQuery = query(repliesRef, orderBy('timestamp', 'asc'));
      const repliesSnapshot = await getDocs(repliesQuery);
      
      const replies = await Promise.all(
        repliesSnapshot.docs.map(async (replyDoc) => {
          const replyData = replyDoc.data();
          let replyUserData = null;
          
          // Fetch reply user data
          try {
            const replyUserDocRef = firestoreDoc(db, 'users', replyData.userId);
            const replyUserDoc = await getDoc(replyUserDocRef);
            
            if (replyUserDoc.exists()) {
              const userData = replyUserDoc.data();
              replyUserData = {
                fullName: userData.fullName || '',
                userName: userData.userName || '',
                profilePictureUrl: userData.profilePictureUrl || '',
                userSubType: userData.userSubType || '',
                idVerificationStatus: userData.idVerificationStatus || ''
              };
            }
          } catch (err) {
            console.error('Error fetching reply user data:', err);
          }
          
          return {
            id: replyDoc.id,
            ...replyData,
            user: replyUserData || {
              fullName: 'Unknown User',
              userName: '',
              profilePictureUrl: '',
              userSubType: '',
              idVerificationStatus: ''
            }
          };
        })
      );
      
      const singleReviewItem = {
        id: reviewDoc.id,
        ...data,
        tags: reviewTags, // Store tags as object for UI
        tagsArray: data.tags || [], // Keep the original array
        user: userDataObj || {
          fullName: 'Unknown User',
          userName: '',
          profilePictureUrl: '',
          userSubType: '',
          idVerificationStatus: ''
        },
        replies: replies,
        hasMoreReplies: false,
        showReplies: replies.length > 0,
        loadingMoreReplies: false,
        repliesCount: replies.length,
        lastReplyDoc: null
      };
      
      setSingleReview(singleReviewItem);
      setIsSingleItemView(true);
      setSingleItemId(reviewId);
      
      // Also check if current user has already submitted review and it's this one
      if (userData?.userId === data.userId) {
        setUserReview({
          rating: data.rating || 0,
          comment: data.comment || '',
          tags: reviewTags
        });
        
        setEditingReview({
          id: reviewDoc.id,
          ...data,
          tags: reviewTags,
          tagsArray: data.tags || []
        });
      }
      
    } catch (err) {
      console.error('Error fetching user data:', err);
      showCustomAlert({
        title: 'Error',
        message: 'Failed to load review details. Please try again.',
        type: 'error'
      });
    }
  } catch (err) {
    console.error('Error fetching single review:', err);
    showCustomAlert({
      title: 'Error',
      message: 'Failed to load review. Please try again.',
      type: 'error'
    });
  } finally {
    setIsLoading(false);
  }
}, [profileData?.userId, userData?.userId, showCustomAlert]);

// Handle back button click
const handleBackToAllReviews = () => {
  setIsSingleItemView(false);
  setSingleReview(null);
  setSingleItemId(null);
  
  // Load the regular review list
  loadReviews(true, 'all', true);
};

// Modify your initial data loading effect to check for an item ID in the URL
useEffect(() => {
  if (profileData?.userId) {
    // Reset state when profile changes
    setDisplayedReviews([]);
    setLastReviewDoc(null);
    setHasMoreReviews(true);
    setActiveTagFilter('all');
    
    // Clear tag cache when profile changes
    clearTagCache();
    
    // Check URL parameters for specific review ID
    const urlParams = new URLSearchParams(window.location.search);
    const section = urlParams.get('section');
    const reviewId = urlParams.get('id');
    
    if (section === 'reviews' && reviewId) {
      // Fetch single review by ID
      fetchSingleReviewById(reviewId);
    } else {
      // Load initial reviews normally
      setIsLoading(true);
      loadReviews(true, 'all', true).finally(() => {
        setIsLoading(false);
      });
    }
    
    // Check if current user has already submitted a review
    const checkExistingReview = async () => {
      if (!userData?.userId) return;
      
      try {
        const db = getFirestoreInstance();
        const reviewsRef = collection(db, 'users', profileData.userId, 'reviews');
        const userReviewQuery = query(reviewsRef, where('userId', '==', userData.userId));
        const userReviewSnapshot = await getDocs(userReviewQuery);
        
        if (!userReviewSnapshot.empty) {
          const reviewData = userReviewSnapshot.docs[0].data();
          
          // Convert tags array to object for the form
          const tagsObject = convertTagsToObject(reviewData.tags);
          
          setUserReview({
            rating: reviewData.rating || 0,
            comment: reviewData.comment || '',
            tags: tagsObject
          });
          
          setEditingReview({
            id: userReviewSnapshot.docs[0].id,
            ...reviewData,
            tags: tagsObject,
            tagsArray: reviewData.tags || []
          });
        } else {
          // Reset user review form
          setUserReview({
            rating: 0,
            comment: '',
            tags: {}
          });
          setEditingReview(null);
        }
      } catch (err) {
        console.error('Error checking existing review:', err);
      }
    };
    
    checkExistingReview();
  }
}, [profileData?.userId, userData?.userId, fetchSingleReviewById]);
  
  // Fetch fresh profile data to get accurate rating counts
  const refreshProfileData = async () => {
    if (!profileData?.userId) return;
    
    try {
      const db = getFirestoreInstance();
      const userDocRef = firestoreDoc(db, 'users', profileData.userId);
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists()) {
        const freshProfileData = userDoc.data();
        setProfileData(prevData => ({
          ...prevData,
          ...freshProfileData
        }));
      }
    } catch (err) {
      console.error('Error refreshing profile data:', err);
    }
  };
  
  // Handle tag selection
  const handleTagSelect = (tag) => {
    setUserReview(prev => {
      const tags = { ...prev.tags };
      tags[tag] = !tags[tag];
      
      return { ...prev, tags };
    });
  };
  
  // Handle rating selection
  const handleRatingClick = (rating) => {
    setUserReview(prev => ({ ...prev, rating }));
  };
  
  // Handle comment input
  const handleCommentChange = (e) => {
    const comment = e.target.value;
    if (comment.length <= 1000) {
      setUserReview(prev => ({ ...prev, comment }));
    }
  };
  
  // Handle tag filter change with loading state
  const handleTagFilterChange = (tag) => {
    if (tag === activeTagFilter) return;
    
    // Show reviews are loading
    setLoadingReviews(true);
    
    // Reset current displayed reviews while loading
    setDisplayedReviews([]);
    
    // Set the active tag immediately for better UX
    setActiveTagFilter(tag);
    
    // Load reviews with new filter (after a brief delay for UI update)
    setTimeout(() => {
      loadReviews(true, tag);
    }, 100);
  };
  
  // Submit review
// Submit review
const handleSubmitReview = async () => {
  if (!userData?.userId || !profileData?.userId || !userReview.rating) {
    showCustomAlert({
      title: 'Error',
      message: 'Please sign in and provide a rating before submitting a review',
      type: 'error'
    });
    return;
  }
  
  setIsSubmitting(true);
  try {
    const db = getFirestoreInstance();
    const userRef = firestoreDoc(db, 'users', profileData.userId);
    
    // Convert tags object to array
    const tagsArray = convertTagsToArray(userReview.tags);
    
    // Create review data with tags array
    const reviewData = {
      userId: userData.userId,
      rating: userReview.rating,
      comment: userReview.comment,
      timestamp: new Date().toISOString(),
      edited: editingReview ? true : false,
      repliesCount: editingReview ? (editingReview.repliesCount || 0) : 0,
      // Store tags as an array
      tags: tagsArray
    };
    
    // Store review ID for local state updates
    let reviewId = editingReview ? editingReview.id : null;
    
    // Run transaction to update user document and add/update review
    await runTransaction(db, async (transaction) => {
      // PERFORM ALL READS FIRST
      const userDoc = await transaction.get(userRef);
      
      if (!userDoc.exists()) {
        throw new Error('User not found');
      }
      
      const userDocData = userDoc.data();
      
      let reviewRef = null;
      
      // If editing, read the existing review document
      if (editingReview) {
        reviewRef = firestoreDoc(db, 'users', profileData.userId, 'reviews', editingReview.id);
        await transaction.get(reviewRef);
      }
      
      // NOW PERFORM ALL WRITES
      
      if (editingReview) {
        // Calculate rating fields to decrement
        const oldRatingField = editingReview.rating === 0.5 ? 'halfStar' :
                        editingReview.rating === 1 ? 'oneStar' :
                        editingReview.rating === 1.5 ? 'oneHalfStar' :
                        editingReview.rating === 2 ? 'twoStar' :
                        editingReview.rating === 2.5 ? 'twoHalfStar' :
                        editingReview.rating === 3 ? 'threeStar' :
                        editingReview.rating === 3.5 ? 'threeHalfStar' :
                        editingReview.rating === 4 ? 'fourStar' :
                        editingReview.rating === 4.5 ? 'fourHalfStar' :
                        'fiveStar';
        
        // Decrement old rating
        const oldRatingCount = (userDocData[oldRatingField] || 0) - 1;
        const totalRatingsScore = (userDocData.totalRatingsScore || 0) - editingReview.rating + userReview.rating;
        
        // Calculate new rating field to increment
        const newRatingField = userReview.rating === 0.5 ? 'halfStar' :
                        userReview.rating === 1 ? 'oneStar' :
                        userReview.rating === 1.5 ? 'oneHalfStar' :
                        userReview.rating === 2 ? 'twoStar' :
                        userReview.rating === 2.5 ? 'twoHalfStar' :
                        userReview.rating === 3 ? 'threeStar' :
                        userReview.rating === 3.5 ? 'threeHalfStar' :
                        userReview.rating === 4 ? 'fourStar' :
                        userReview.rating === 4.5 ? 'fourHalfStar' :
                        'fiveStar';
        
        // Create update object for edited review
        const updateObj = {
          [oldRatingField]: Math.max(0, oldRatingCount),
          [newRatingField]: (userDocData[newRatingField] || 0) + 1,
          totalRatingsScore,
          averageRating: totalRatingsScore / (userDocData.totalRatingsCount || 1)
        };
        
        // Update user document
        transaction.update(userRef, updateObj);
        
        // Update review document
        transaction.update(reviewRef, reviewData);
      } else {
        // Adding a new review
        
        // Calculate rating field
        const ratingField = userReview.rating === 0.5 ? 'halfStar' :
                        userReview.rating === 1 ? 'oneStar' :
                        userReview.rating === 1.5 ? 'oneHalfStar' :
                        userReview.rating === 2 ? 'twoStar' :
                        userReview.rating === 2.5 ? 'twoHalfStar' :
                        userReview.rating === 3 ? 'threeStar' :
                        userReview.rating === 3.5 ? 'threeHalfStar' :
                        userReview.rating === 4 ? 'fourStar' :
                        userReview.rating === 4.5 ? 'fourHalfStar' :
                        'fiveStar';
        
        const totalRatingsCount = (userDocData.totalRatingsCount || 0) + 1;
        const totalRatingsScore = (userDocData.totalRatingsScore || 0) + userReview.rating;
        const averageRating = totalRatingsScore / totalRatingsCount;
        
        // Create update object for new review
        const updateObj = {
          [ratingField]: (userDocData[ratingField] || 0) + 1,
          totalRatingsCount,
          totalRatingsScore,
          averageRating
        };
        
        // Update user document
        transaction.update(userRef, updateObj);
        
        // Add review to subcollection
        const reviewsColRef = collection(userRef, 'reviews');
        const newReviewRef = firestoreDoc(reviewsColRef);
        transaction.set(newReviewRef, reviewData);
        
        // Store the new review ID for local state updates
        reviewId = newReviewRef.id;
      }
    });
    
    // Send appropriate notification based on whether this is a new review or an update
    if (editingReview) {
      // Send notification for review update
      await createNotification(profileData.userId, 'general', {
        type: 'REVIEW_UPDATED',
        title: 'Review Updated',
        message: `${userData.fullName || 'Someone'} has updated their ${userReview.rating}-star review on your profile.`,
        data: {
          userId: userData.userId,
          profileId: profileData.userId,
          reviewId: editingReview.id,
          rating: userReview.rating,
          _displayData: {
            fullName: userData.fullName,
            userName: userData.userName,
            profilePictureUrl: userData.profilePictureUrl || ''
          }
        }
      });
    } else {
      // Create notification for the organization/vendor for new review
      await createNotification(profileData.userId, 'general', {
        type: 'NEW_REVIEW',
        title: 'New Review',
        message: `${userData.fullName || 'Someone'} has left a ${userReview.rating}-star review.`,
        data: {
          userId: userData.userId,
          profileId: profileData.userId,
          reviewId: reviewId,
          rating: userReview.rating,
          _displayData: {
            fullName: userData.fullName,
            userName: userData.userName,
            profilePictureUrl: userData.profilePictureUrl || ''
          }
        }
      });
    }
    
    // Refresh profile data to get updated rating counts
    await refreshProfileData();
    
    // Clear tag cache since data has changed
    clearTagCache();
    
    // Update local state to show the new/edited review immediately
    if (editingReview) {
      // Update the review in the list
      setDisplayedReviews(prev => 
        prev.map(review => 
          review.id === editingReview.id 
            ? {
                ...review,
                rating: userReview.rating,
                comment: userReview.comment,
                tags: userReview.tags,
                tagsArray: convertTagsToArray(userReview.tags),
                edited: true,
                timestamp: new Date().toISOString()
              }
            : review
        )
      );
      
      // Also update in the full reviews list
      setReviews(prev => 
        prev.map(review => 
          review.id === editingReview.id 
            ? {
                ...review,
                rating: userReview.rating,
                comment: userReview.comment,
                tags: userReview.tags,
                tagsArray: convertTagsToArray(userReview.tags),
                edited: true,
                timestamp: new Date().toISOString()
              }
            : review
        )
      );
      
      // Update editing state
      setEditingReview(prev => ({
        ...prev,
        rating: userReview.rating,
        comment: userReview.comment,
        tags: userReview.tags,
        tagsArray: convertTagsToArray(userReview.tags),
        edited: true,
        timestamp: new Date().toISOString()
      }));
    } else {
      // Add new review to the top of the list
      const newReview = {
        id: reviewId,
        ...reviewData,
        tags: userReview.tags,
        tagsArray: convertTagsToArray(userReview.tags),
        user: {
          fullName: userData.fullName || '',
          userName: userData.userName || '',
          profilePictureUrl: userData.profilePictureUrl || '',
          userSubType: userData.userSubType || '',
          idVerificationStatus: userData.idVerificationStatus || ''
        },
        replies: [],
        hasMoreReplies: false,
        showReplies: false,
        repliesCount: 0,
        lastReplyDoc: null
      };
      
      setDisplayedReviews(prev => [newReview, ...prev]);
      setReviews(prev => [newReview, ...prev]);
      
      // Update editing state for future edits
      setEditingReview(newReview);
      
      // Update available tags
      if (tagsArray.length > 0) {
        setAvailableTags(prev => {
          const newTags = new Set([...prev]);
          tagsArray.forEach(tag => newTags.add(tag));
          return Array.from(newTags);
        });
      }
    }
    
    // Reload current filter view to ensure consistency
    loadReviews(true, activeTagFilter, true);
    
    // Show success message
    showCustomAlert({
      title: 'Success',
      message: editingReview ? 'Your review has been updated!' : 'Thank you for your review!',
      type: 'success'
    });
    
    // Hide review form
    setShowReviewForm(false);
    
  } catch (err) {
    console.error('Error submitting review:', err);
    showCustomAlert({
      title: 'Error',
      message: 'Failed to submit review. Please try again.',
      type: 'error'
    });
  } finally {
    setIsSubmitting(false);
  }
};
  
  // Delete review
  const handleDeleteReview = async (reviewId) => {
    if (!window.confirm('Are you sure you want to delete this review?')) {
      return;
    }
    
    try {
      const db = getFirestoreInstance();
      const reviewRef = firestoreDoc(db, 'users', profileData.userId, 'reviews', reviewId);
      
      // Get the review data first to adjust ratings
      const reviewDoc = await getDoc(reviewRef);
      
      if (reviewDoc.exists()) {
        const reviewData = reviewDoc.data();
        const userRef = firestoreDoc(db, 'users', profileData.userId);
        
        // Run transaction to update user document and delete review
        await runTransaction(db, async (transaction) => {
          // PERFORM ALL READS FIRST
          const userDoc = await transaction.get(userRef);
          
          if (!userDoc.exists()) {
            throw new Error('User not found');
          }
          
          const userData = userDoc.data();
          
          // Read all replies to delete
          const repliesRef = collection(db, 'users', profileData.userId, 'reviews', reviewId, 'replies');
          const repliesSnapshot = await getDocs(repliesRef);
          
          // NOW PERFORM ALL WRITES
          
          // Calculate rating field to decrement
          const ratingField = reviewData.rating === 0.5 ? 'halfStar' :
                          reviewData.rating === 1 ? 'oneStar' :
                          reviewData.rating === 1.5 ? 'oneHalfStar' :
                          reviewData.rating === 2 ? 'twoStar' :
                          reviewData.rating === 2.5 ? 'twoHalfStar' :
                          reviewData.rating === 3 ? 'threeStar' :
                          reviewData.rating === 3.5 ? 'threeHalfStar' :
                          reviewData.rating === 4 ? 'fourStar' :
                          reviewData.rating === 4.5 ? 'fourHalfStar' :
                          'fiveStar';
          
          const totalRatingsCount = Math.max(0, (userData.totalRatingsCount || 0) - 1);
          const totalRatingsScore = Math.max(0, (userData.totalRatingsScore || 0) - reviewData.rating);
          const averageRating = totalRatingsCount > 0 ? totalRatingsScore / totalRatingsCount : 0;
          
          // Create update object
          const updateObj = {
            [ratingField]: Math.max(0, (userData[ratingField] || 0) - 1),
            totalRatingsCount,
            totalRatingsScore,
            averageRating
          };
          
          // Update user document
          transaction.update(userRef, updateObj);
          
          // Delete all replies
          repliesSnapshot.docs.forEach(doc => {
            transaction.delete(firestoreDoc(repliesRef, doc.id));
          });
          
          // Delete review document
          transaction.delete(reviewRef);
        });
        
        // Refresh profile data to get updated rating counts
        await refreshProfileData();
        
        // Clear tag cache since data has changed
        clearTagCache();
        
        // Update local state
        setDisplayedReviews(prev => prev.filter(review => review.id !== reviewId));
        setReviews(prev => prev.filter(review => review.id !== reviewId));
        
        // Reset editing state if deleting the review being edited
        if (editingReview && editingReview.id === reviewId) {
          setEditingReview(null);
          setUserReview({
            rating: 0,
            comment: '',
            tags: {}
          });
        }
        
        // Re-fetch available tags in case this was the only review with certain tags
        fetchAvailableTags();
        
        // Reload current filter view to ensure consistency
        loadReviews(true, activeTagFilter, true);
        
        showCustomAlert({
          title: 'Success',
          message: 'Review deleted successfully',
          type: 'success'
        });
      }
    } catch (err) {
      console.error('Error deleting review:', err);
      showCustomAlert({
        title: 'Error',
        message: 'Failed to delete review. Please try again.',
        type: 'error'
      });
    }
  };
  
  // Load more replies for a review
  const handleLoadMoreReplies = async (reviewId, lastReplyDoc) => {
    if (!reviewId || !lastReplyDoc) return;
    
    // Update loading state for this review
    setDisplayedReviews(prev => 
      prev.map(review => 
        review.id === reviewId 
          ? { ...review, loadingMoreReplies: true }
          : review
      )
    );
    
    // IMPORTANT: Also update singleReview loading state if in single item view
    if (isSingleItemView && singleReview && singleReview.id === reviewId) {
      setSingleReview(prev => ({
        ...prev,
        loadingMoreReplies: true
      }));
    }
    
    try {
      const db = getFirestoreInstance();
      const repliesRef = collection(db, 'users', profileData.userId, 'reviews', reviewId, 'replies');
      const repliesQuery = query(repliesRef, orderBy('timestamp', 'asc'), startAfter(lastReplyDoc), limit(2));
      const repliesSnapshot = await getDocs(repliesQuery);
      
      if (!repliesSnapshot.empty) {
        const newReplies = await Promise.all(
          repliesSnapshot.docs.map(async (replyDoc) => {
            const replyData = replyDoc.data();
            let replyUserData = null;
            
            // Fetch reply user data
            try {
              const replyUserDocRef = firestoreDoc(db, 'users', replyData.userId);
              const replyUserDoc = await getDoc(replyUserDocRef);
              
              if (replyUserDoc.exists()) {
                const userData = replyUserDoc.data();
                replyUserData = {
                  fullName: userData.fullName || '',
                  userName: userData.userName || '',
                  profilePictureUrl: userData.profilePictureUrl || '',
                  userSubType: userData.userSubType || '',
                  idVerificationStatus: userData.idVerificationStatus || ''
                };
              }
            } catch (err) {
              console.error('Error fetching reply user data:', err);
            }
            
            return {
              id: replyDoc.id,
              ...replyData,
              user: replyUserData || {
                fullName: 'Unknown User',
                userName: '',
                profilePictureUrl: '',
                userSubType: '',
                idVerificationStatus: ''
              }
            };
          })
        );
        
        // Update the review with new replies and lastReplyDoc
        const updateReviews = prev => 
          prev.map(review => 
            review.id === reviewId 
              ? {
                  ...review,
                  replies: [...review.replies, ...newReplies],
                  lastReplyDoc: repliesSnapshot.docs[repliesSnapshot.docs.length - 1],
                  hasMoreReplies: repliesSnapshot.docs.length === 2,
                  loadingMoreReplies: false
                }
              : review
          );
        
        setDisplayedReviews(updateReviews);
        setReviews(updateReviews);
        
        // Also update the cached review data
        setTagFilteredReviews(prev => {
          const newCache = { ...prev };
          
          Object.keys(newCache).forEach(tag => {
            if (newCache[tag]) {
              newCache[tag] = newCache[tag].map(review => 
                review.id === reviewId 
                  ? {
                      ...review,
                      replies: [...review.replies, ...newReplies],
                      lastReplyDoc: repliesSnapshot.docs[repliesSnapshot.docs.length - 1],
                      hasMoreReplies: repliesSnapshot.docs.length === 2,
                      loadingMoreReplies: false
                    }
                  : review
              );
            }
          });
          
          return newCache;
        });
        
        // IMPORTANT: Also update singleReview if in single item view
        if (isSingleItemView && singleReview && singleReview.id === reviewId) {
          setSingleReview(prev => ({
            ...prev,
            replies: [...prev.replies, ...newReplies],
            lastReplyDoc: repliesSnapshot.docs[repliesSnapshot.docs.length - 1],
            hasMoreReplies: repliesSnapshot.docs.length === 2,
            loadingMoreReplies: false
          }));
        }
        
      } else {
        // No more replies
        const updateReviews = prev => 
          prev.map(review => 
            review.id === reviewId 
              ? { ...review, hasMoreReplies: false, loadingMoreReplies: false }
              : review
          );
        
        setDisplayedReviews(updateReviews);
        setReviews(updateReviews);
        
        // Also update the cached review data
        setTagFilteredReviews(prev => {
          const newCache = { ...prev };
          
          Object.keys(newCache).forEach(tag => {
            if (newCache[tag]) {
              newCache[tag] = newCache[tag].map(review => 
                review.id === reviewId 
                  ? { ...review, hasMoreReplies: false, loadingMoreReplies: false }
                  : review
              );
            }
          });
          
          return newCache;
        });
        
        // IMPORTANT: Also update singleReview if in single item view
        if (isSingleItemView && singleReview && singleReview.id === reviewId) {
          setSingleReview(prev => ({
            ...prev,
            hasMoreReplies: false,
            loadingMoreReplies: false
          }));
        }
      }
    } catch (err) {
      console.error('Error loading more replies:', err);
      
      // Reset loading state
      setDisplayedReviews(prev => 
        prev.map(review => 
          review.id === reviewId 
            ? { ...review, loadingMoreReplies: false }
            : review
        )
      );
      
      // IMPORTANT: Also reset singleReview loading state if in single item view
      if (isSingleItemView && singleReview && singleReview.id === reviewId) {
        setSingleReview(prev => ({
          ...prev,
          loadingMoreReplies: false
        }));
      }
    }
  };
  
  // Toggle showing/hiding replies
  const handleToggleReplies = (reviewId) => {
    // Update regular review lists
    const updateReviews = prev => 
      prev.map(review => 
        review.id === reviewId 
          ? { ...review, showReplies: !review.showReplies }
          : review
      );
    
    setDisplayedReviews(updateReviews);
    setReviews(updateReviews);
    
    // Also update the cached review data
    setTagFilteredReviews(prev => {
      const newCache = { ...prev };
      
      Object.keys(newCache).forEach(tag => {
        if (newCache[tag]) {
          newCache[tag] = newCache[tag].map(review => 
            review.id === reviewId 
              ? { ...review, showReplies: !review.showReplies }
              : review
          );
        }
      });
      
      return newCache;
    });
    
    // IMPORTANT: Also update the single review item if we're in single item view
    if (isSingleItemView && singleReview && singleReview.id === reviewId) {
      setSingleReview(prev => ({
        ...prev,
        showReplies: !prev.showReplies
      }));
    }
  };
  
  // Add reply to a review
  const handleAddReply = async (reviewId, replyText) => {
    if (!userData?.userId || !replyText.trim()) return;
    
    try {
      const db = getFirestoreInstance();
      const repliesRef = collection(db, 'users', profileData.userId, 'reviews', reviewId, 'replies');
      const reviewRef = firestoreDoc(db, 'users', profileData.userId, 'reviews', reviewId);
      
      // Add reply
      const replyData = {
        userId: userData.userId,
        text: replyText.trim(),
        timestamp: new Date().toISOString()
      };
      
      const replyDocRef = await addDoc(repliesRef, replyData);
      
      // Update review's replies count
      const reviewDoc = await getDoc(reviewRef);
      if (reviewDoc.exists()) {
        const reviewData = reviewDoc.data();
        await updateDoc(reviewRef, {
          repliesCount: (reviewData.repliesCount || 0) + 1
        });
      }
      
      // Add the new reply to the UI
      const newReply = {
        id: replyDocRef.id,
        ...replyData,
        user: {
          fullName: userData.fullName || '',
          userName: userData.userName || '',
          profilePictureUrl: userData.profilePictureUrl || '',
          userSubType: userData.userSubType || '',
          idVerificationStatus: userData.idVerificationStatus || ''
        }
      };
      
      // Update the review with the new reply
      const updateReviews = prev => 
        prev.map(review => 
          review.id === reviewId 
            ? {
                ...review,
                replies: [...review.replies, newReply],
                repliesCount: (review.repliesCount || 0) + 1,
                showReplies: true
              }
            : review
        );
      
      setDisplayedReviews(updateReviews);
      setReviews(updateReviews);
      
      // Also update the cached review data
      setTagFilteredReviews(prev => {
        const newCache = { ...prev };
        
        Object.keys(newCache).forEach(tag => {
          if (newCache[tag]) {
            newCache[tag] = newCache[tag].map(review => 
              review.id === reviewId 
                ? {
                    ...review,
                    replies: [...review.replies, newReply],
                    repliesCount: (review.repliesCount || 0) + 1,
                    showReplies: true
                  }
                : review
            );
          }
        });
        
        return newCache;
      });
      
      // IMPORTANT: Also update singleReview if in single item view
      if (isSingleItemView && singleReview && singleReview.id === reviewId) {
        setSingleReview(prev => ({
          ...prev,
          replies: [...prev.replies, newReply],
          repliesCount: (prev.repliesCount || 0) + 1,
          showReplies: true
        }));
      }
      
      // Create notification for the review author
      const reviewDoc2 = await getDoc(reviewRef);
      if (reviewDoc2.exists()) {
        const reviewData = reviewDoc2.data();
        
        if (reviewData.userId !== userData.userId) {
          await createNotification(reviewData.userId, 'general', {
            type: 'REVIEW_REPLY',
            title: 'Reply to Your Review',
            message: `${userData.fullName || 'Someone'} has replied to your review.`,
            data: {
              userId: userData.userId,
              profileId: profileData.userId,
              reviewId: reviewId,
              replyId: replyDocRef.id,
              _displayData: {
                fullName: userData.fullName,
                userName: userData.userName,
                profilePictureUrl: userData.profilePictureUrl || ''
              }
            }
          });
        }
      }
      
      showCustomAlert({
        title: 'Success',
        message: 'Reply added successfully',
        type: 'success'
      });
    } catch (err) {
      console.error('Error adding reply:', err);
      showCustomAlert({
        title: 'Error',
        message: 'Failed to add reply. Please try again.',
        type: 'error'
      });
    }
  };
  
  // 3. Update handleDeleteReply to also update singleReview
  const handleDeleteReply = async (reviewId, replyId) => {
    if (!window.confirm('Are you sure you want to delete this reply?')) {
      return;
    }
    
    try {
      const db = getFirestoreInstance();
      const replyRef = firestoreDoc(db, 'users', profileData.userId, 'reviews', reviewId, 'replies', replyId);
      const reviewRef = firestoreDoc(db, 'users', profileData.userId, 'reviews', reviewId);
      
      // Delete reply
      await deleteDoc(replyRef);
      
      // Update review's replies count
      const reviewDoc = await getDoc(reviewRef);
      if (reviewDoc.exists()) {
        const reviewData = reviewDoc.data();
        await updateDoc(reviewRef, {
          repliesCount: Math.max(0, (reviewData.repliesCount || 0) - 1)
        });
      }
      
      // Update UI
      const updateReviews = prev => 
        prev.map(review => 
          review.id === reviewId 
            ? {
                ...review,
                replies: review.replies.filter(reply => reply.id !== replyId),
                repliesCount: Math.max(0, (review.repliesCount || 0) - 1)
              }
            : review
        );
      
      setDisplayedReviews(updateReviews);
      setReviews(updateReviews);
      
      // Also update the cached review data
      setTagFilteredReviews(prev => {
        const newCache = { ...prev };
        
        Object.keys(newCache).forEach(tag => {
          if (newCache[tag]) {
            newCache[tag] = newCache[tag].map(review => 
              review.id === reviewId 
                ? {
                    ...review,
                    replies: review.replies.filter(reply => reply.id !== replyId),
                    repliesCount: Math.max(0, (review.repliesCount || 0) - 1)
                  }
                : review
            );
          }
        });
        
        return newCache;
      });
      
      // IMPORTANT: Also update singleReview if in single item view
      if (isSingleItemView && singleReview && singleReview.id === reviewId) {
        setSingleReview(prev => ({
          ...prev,
          replies: prev.replies.filter(reply => reply.id !== replyId),
          repliesCount: Math.max(0, (prev.repliesCount || 0) - 1)
        }));
      }
      
      showCustomAlert({
        title: 'Success',
        message: 'Reply deleted successfully',
        type: 'success'
      });
    } catch (err) {
      console.error('Error deleting reply:', err);
      showCustomAlert({
        title: 'Error',
        message: 'Failed to delete reply. Please try again.',
        type: 'error'
      });
    }
  };
  
  // Handle editing a review
  const handleEditReview = (review) => {
    setEditingReview(review);
    setUserReview({
      rating: review.rating,
      comment: review.comment,
      tags: review.tags || {}
    });
    setShowReviewForm(true);
  };
  
  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(date);
  };
  
  // Render star rating
  const renderStarRating = (rating, size = 'md') => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;
    
    // Full stars
    for (let i = 0; i < fullStars; i++) {
      stars.push(
        <FontAwesomeIcon 
          key={`full-${i}`} 
          icon={faStar} 
          className={`${styles.star} ${styles[`star-${size}`]}`} 
        />
      );
    }
    
    // Half star
    if (hasHalfStar) {
      stars.push(
        <FontAwesomeIcon 
          key="half" 
          icon={faStarHalfAlt} 
          className={`${styles.star} ${styles[`star-${size}`]}`} 
        />
      );
    }
    
    // Empty stars
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(
        <FontAwesomeIcon 
          key={`empty-${i}`} 
          icon={farStar} 
          className={`${styles.emptyStar} ${styles[`star-${size}`]}`} 
        />
      );
    }
    
    return stars;
  };
  
  // Render the review form modal
  const renderReviewForm = () => {
    return (
      <div className={styles.modalOverlay}>
        <div className={styles.modalContent}>
          <div className={styles.modalHeader}>
            <h2>{editingReview ? 'Edit Your Review' : 'Write a Review'}</h2>
            <button 
              className={styles.closeButton}
              onClick={() => {
                setShowReviewForm(false);
                if (!editingReview) {
                  setUserReview({
                    rating: 0,
                    comment: '',
                    tags: {}
                  });
                }
              }}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
          
          <div className={styles.reviewForm}>
            {/* Rating */}
            <div className={styles.ratingSelector}>
              <p className={styles.ratingLabel}>Your Rating:</p>
              <div className={styles.ratingStars}>
                {[0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5].map(rating => (
                  <button
                    key={rating}
                    className={styles.ratingButton}
                    onClick={() => handleRatingClick(rating)}
                    onMouseEnter={() => setHoverRating(rating)}
                    onMouseLeave={() => setHoverRating(0)}
                  >
                    {/* Half-star positions */}
                    {rating % 1 !== 0 ? (
                      <>
                        <div className={styles.halfStarContainer}>
                          <FontAwesomeIcon 
                            icon={(hoverRating || userReview.rating) >= rating - 0.5 ? faStar : farStar} 
                            className={styles.halfStarLeft}
                          />
                          <FontAwesomeIcon 
                            icon={(hoverRating || userReview.rating) >= rating ? faStar : farStar} 
                            className={styles.halfStarRight}
                          />
                        </div>
                      </>
                    ) : (
                      <FontAwesomeIcon 
                        icon={(hoverRating || userReview.rating) >= rating ? faStar : farStar} 
                        className={styles.star}
                      />
                    )}
                  </button>
                ))}
              </div>
              <span className={styles.selectedRating}>
                {userReview.rating > 0 ? `${userReview.rating.toFixed(1)} Stars` : 'Select Rating'}
              </span>
            </div>
            
            {/* Comment */}
            <div className={styles.commentSection}>
              <textarea
                placeholder="Share your experience (optional, max 1000 characters)"
                value={userReview.comment}
                onChange={handleCommentChange}
                className={styles.commentInput}
                maxLength={1000}
              />
              <div className={styles.characterCount}>
                {userReview.comment.length}/1000
              </div>
            </div>
            
            {/* Tags */}
            <div className={styles.tagsSection}>
              <h3 className={styles.tagsTitle}>Select Tags (Optional)</h3>
              
              {/* Tag Categories */}
              <div className={styles.tagCategoriesContainer}>
                {Object.entries(tagCategories).map(([key, category]) => (
                  <div key={key} className={styles.tagCategory}>
                    <h4 className={styles.categoryTitle}>{category.title}</h4>
                    <div className={styles.tagsList}>
                      {category.tags.map(tag => (
                        <button
                          key={tag}
                          className={`${styles.tagButton} ${userReview.tags[tag] ? styles.tagSelected : ''}`}
                          onClick={() => handleTagSelect(tag)}
                        >
                          {tag}
                          {userReview.tags[tag] && (
                            <FontAwesomeIcon icon={faCheck} className={styles.tagCheckIcon} />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Submit Button */}
            <div className={styles.formActions}>
              <button
                className={styles.cancelButton}
                onClick={() => {
                  setShowReviewForm(false);
                  if (!editingReview) {
                    setUserReview({
                      rating: 0,
                      comment: '',
                      tags: {}
                    });
                  }
                }}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                className={styles.submitButton}
                onClick={handleSubmitReview}
                disabled={isSubmitting || !userReview.rating}
              >
                {isSubmitting ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin />
                    <span>Submitting...</span>
                  </>
                ) : (
                  <span>{editingReview ? 'Update Review' : 'Submit Review'}</span>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };
  
  // Render tag filters
  const renderTagFilters = () => {
    // Combine 'all' with available tags (limit to top 8 tags)
    const displayableTags = ['all', ...availableTags.slice(0, 8)];
    
    return (
      <div className={styles.tagFilters}>
        <div className={styles.tagFiltersHeader}>
          <h3>Filter Reviews</h3>
          <FontAwesomeIcon icon={faFilter} className={styles.filterIcon} />
        </div>
        <div className={styles.tagFiltersList}>
          {displayableTags.map(tag => (
            <button
              key={tag}
              className={`${styles.tagFilterButton} ${activeTagFilter === tag ? styles.activeTagFilter : ''}`}
              onClick={() => handleTagFilterChange(tag)}
            >
              {tag === 'all' ? 'All Reviews' : tag}
            </button>
          ))}
        </div>
      </div>
    );
  };
  
  // Render reply form
  const ReplyForm = ({ reviewId, onSubmit }) => {
    const [replyText, setReplyText] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    const handleSubmit = async () => {
      if (!replyText.trim()) return;
      
      setIsSubmitting(true);
      try {
        await onSubmit(reviewId, replyText);
        setReplyText('');
      } finally {
        setIsSubmitting(false);
      }
    };
    
    return (
      <div className={styles.replyForm}>
        <textarea
          placeholder="Write a reply..."
          value={replyText}
          onChange={(e) => setReplyText(e.target.value)}
          className={styles.replyInput}
          maxLength={500}
        />
        <div className={styles.replyFormActions}>
          <div className={styles.characterCount}>
            {replyText.length}/500
          </div>
          <button
            className={styles.replyButton}
            onClick={handleSubmit}
            disabled={isSubmitting || !replyText.trim()}
          >
            {isSubmitting ? (
              <FontAwesomeIcon icon={faSpinner} spin />
            ) : (
              <span>Reply</span>
            )}
          </button>
        </div>
      </div>
    );
  };
  
  // Render review item
  const ReviewItem = ({ review }) => {
    const [showReplyForm, setShowReplyForm] = useState(false);
    
    return (
      <div 
      key={review.id} 
      className={styles.reviewItem}
      id={`review-${review.id}`}  // Add this ID
    >
        <div className={styles.reviewHeader}>
          <img
            src={review.user.profilePictureUrl || defaultUserImage}
            alt={review.user.fullName}
            className={styles.userAvatar}
          />
          <div 
  className={styles.userInfo}
  onClick={() => navigateToUserProfile(review.user.userName)}
  style={{ cursor: 'pointer' }}
>
  <div className={styles.userName}>
    {review.user.fullName}
    {review.user.idVerificationStatus === 'Verified' && (
      <img 
        src={verifiedBadge} 
        alt="Verified" 
        className={styles.verifiedBadge} 
      />
    )}
  </div>
  <div className={styles.userDetails}>
    @{review.user.userName} • {review.user.userSubType}
  </div>
</div>
          <div className={styles.reviewMeta}>
            <div className={styles.reviewRating}>
              {renderStarRating(review.rating, 'sm')}
              <span className={styles.ratingValue}>{review.rating.toFixed(1)}</span>
            </div>
            <div className={styles.reviewDate}>
              {formatDate(review.timestamp)}
              {review.edited && <span className={styles.editedBadge}>(Edited)</span>}
            </div>
          </div>
        </div>
        
        {review.comment && (
          <div className={styles.reviewComment}>
            {review.comment}
          </div>
        )}
        
        {review.tags && Object.keys(review.tags).length > 0 && (
          <div className={styles.reviewTags}>
            {Object.entries(review.tags)
              .filter(([_, value]) => value === true)
              .map(([tag]) => (
                <span key={tag} className={styles.reviewTag}>
                  {tag}
                </span>
              ))}
          </div>
        )}
        
        <div className={styles.reviewActions}>
          {isOwnProfile && (
            <button
              className={styles.replyButton}
              onClick={() => setShowReplyForm(!showReplyForm)}
            >
              <FontAwesomeIcon icon={faReply} />
              <span>{showReplyForm ? 'Cancel Reply' : 'Reply'}</span>
            </button>
          )}
          
          {userData?.userId === review.userId && (
            <>
              <button
                className={styles.editButton}
                onClick={() => handleEditReview(review)}
              >
                <FontAwesomeIcon icon={faEdit} />
                <span>Edit</span>
              </button>
              <button
                className={styles.deleteButton}
                onClick={() => handleDeleteReview(review.id)}
              >
                <FontAwesomeIcon icon={faTrash} />
                <span>Delete</span>
              </button>
            </>
          )}
        </div>
        
        {showReplyForm && (
          <ReplyForm 
            reviewId={review.id} 
            onSubmit={handleAddReply}
          />
        )}
        
        {/* Replies Section */}
        {review.repliesCount > 0 && (
          <div className={styles.repliesSection}>
            <button
              className={styles.toggleRepliesButton}
              onClick={() => handleToggleReplies(review.id)}
            >
              <FontAwesomeIcon 
                icon={review.showReplies ? faChevronUp : faChevronDown} 
                className={styles.toggleIcon}
              />
              <span>
                {review.showReplies 
                  ? 'Hide Replies' 
                  : `Show Replies (${review.repliesCount})`}
              </span>
            </button>
            
            {review.showReplies && (
              <div className={styles.repliesList}>
                {review.replies.map(reply => (
                  <div key={reply.id} className={styles.replyItem}>
                    <div className={styles.replyHeader}>
                      <img
                        src={reply.user.profilePictureUrl || defaultUserImage}
                        alt={reply.user.fullName}
                        className={styles.replyUserAvatar}
                      />
                      <div 
  className={styles.replyUserInfo}
  onClick={() => navigateToUserProfile(reply.user.userName)}
  style={{ cursor: 'pointer' }}
>
                        <div className={styles.replyUserName}>
                          {reply.user.fullName}
                          {reply.user.idVerificationStatus === 'Verified' && (
                            <img 
                              src={verifiedBadge} 
                              alt="Verified" 
                              className={styles.verifiedBadgeSmall} 
                            />
                          )}
                          <span className={styles.replyUserRole}>
                            • {reply.user.userSubType}
                          </span>
                        </div>
                        <div className={styles.replyTimestamp}>
                          {formatDate(reply.timestamp)}
                        </div>
                      </div>
                      
                      {(isOwnProfile || userData?.userId === reply.userId) && (
                        <button
                          className={styles.deleteReplyButton}
                          onClick={() => handleDeleteReply(review.id, reply.id)}
                          title="Delete Reply"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      )}
                    </div>
                    <div className={styles.replyText}>
                      {reply.text}
                    </div>
                  </div>
                ))}
                
                {review.hasMoreReplies && (
                  <button
                    className={styles.loadMoreRepliesButton}
                    onClick={() => handleLoadMoreReplies(review.id, review.lastReplyDoc)}
                    disabled={review.loadingMoreReplies}
                  >
                    {review.loadingMoreReplies ? (
                      <>
                        <FontAwesomeIcon icon={faSpinner} spin />
                        <span>Loading...</span>
                      </>
                    ) : (
                      <span>Load More Replies</span>
                    )}
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };
  
  // Render rating statistics
  const renderRatingStats = () => {
    const averageRating = calculateAverageRating(profileData);
    const totalRatings = profileData.totalRatingsCount || 0;
    
    // Calculate percentage for each star rating
    const calculatePercentage = (count) => {
      if (!totalRatings) return 0;
      return (count / totalRatings) * 100;
    };
    
    const ratingCounts = {
      5: profileData.fiveStar || 0,
      4.5: profileData.fourHalfStar || 0,
      4: profileData.fourStar || 0,
      3.5: profileData.threeHalfStar || 0,
      3: profileData.threeStar || 0,
      2.5: profileData.twoHalfStar || 0,
      2: profileData.twoStar || 0,
      1.5: profileData.oneHalfStar || 0,
      1: profileData.oneStar || 0,
      0.5: profileData.halfStar || 0
    };
    
    // Group ratings for the chart
    const groupedRatings = {
      5: ratingCounts[5],
      4: ratingCounts[4.5] + ratingCounts[4],
      3: ratingCounts[3.5] + ratingCounts[3],
      2: ratingCounts[2.5] + ratingCounts[2],
      1: ratingCounts[1.5] + ratingCounts[1] + ratingCounts[0.5]
    };
    
    return (
      <div className={styles.ratingStats}>
        <div className={styles.ratingOverview}>
          <div className={styles.averageRating}>
            <div className={styles.averageRatingValue}>
              {averageRating.toFixed(1)}
            </div>
            <div className={styles.ratingStars}>
              {renderStarRating(averageRating, 'lg')}
            </div>
            <div className={styles.totalRatings}>
              {totalRatings} {totalRatings === 1 ? 'review' : 'reviews'}
            </div>
          </div>
          <div className={styles.ratingDistribution}>
            {[5, 4, 3, 2, 1].map(rating => (
              <div key={rating} className={styles.ratingBar}>
                <div className={styles.ratingLabel}>
                  {rating} {rating === 1 ? 'star' : 'stars'}
                </div>
                <div className={styles.ratingBarContainer}>
                  <div 
                    className={styles.ratingBarFill}
                    style={{width: `${calculatePercentage(groupedRatings[rating])}%`}}
                  ></div>
                </div>
                <div className={styles.ratingCount}>
                  {groupedRatings[rating]}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };
  
  return (
  <div className={styles.ratingsReviewsSection}>
    <h2 className={styles.sectionTitle}>Ratings & Reviews</h2>
    
    {/* Rating Statistics - always show this */}
    {renderRatingStats()}
    
    {/* Single Review View */}
    {isSingleItemView && singleReview ? (
      <div className={styles.singleItemView}>
        <div className={styles.singleItemHeader}>
          <button 
            className={styles.backButton}
            onClick={handleBackToAllReviews}
          >
            ← Back to all reviews
          </button>
        </div>
        
        <ReviewItem review={singleReview} />
      </div>
    ) : (
      /* Regular reviews list view - keep your existing code here */
      <>
        {/* Tag Filters */}
        {availableTags.length > 0 && renderTagFilters()}
        
        {/* Add Review Button */}
        {userData && !isOwnProfile && (
          <button 
            className={styles.addReviewButton}
            onClick={() => setShowReviewForm(true)}
          >
            <FontAwesomeIcon icon={faPlus} />
            <span>{editingReview ? 'Edit Your Review' : 'Write a Review'}</span>
          </button>
        )}
        
        {/* Reviews List with improved loading state */}
        {isLoading || (loadingReviews && displayedReviews.length === 0) ? (
          <div className={styles.loadingContainer}>
            <div className={styles.spinner}></div>
            <p>Loading reviews...</p>
          </div>
        ) : displayedReviews.length > 0 ? (
          <div className={styles.reviewsList}>
            {displayedReviews.map(review => (
              <ReviewItem key={review.id} review={review} />
            ))}
            
            {hasMoreReviews && (
              <button
                className={styles.loadMoreButton}
                onClick={() => loadReviews(false, activeTagFilter)}
                disabled={loadingReviews}
              >
                {loadingReviews ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin />
                    <span>Loading...</span>
                  </>
                ) : (
                  <span>Load More Reviews</span>
                )}
              </button>
            )}
          </div>
        ) : (
          <div className={styles.emptyState}>
            <p>No reviews yet. Be the first to leave a review!</p>
          </div>
        )}
      </>
    )}
    
    {/* Review Form Modal */}
    {showReviewForm && renderReviewForm()}
  </div>
);
};

export default RatingsReviewsSection;